import React, { useState, useMemo, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';

// --- Components ---
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import { Card, Collapse, IconButton, CircularProgress, Tabs, Tab, Grid, Divider, Checkbox, Chip, TextField, InputAdornment, Pagination } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import { grey } from '@mui/material/colors';
import InfoIcon from '@mui/icons-material/Info';

// --- Contexts ---
import { useVisibility } from 'context/visibilityContext';
import { renderFields } from '../utils/fieldRenderer';

// --- Helper function for formatting field names ---
const formatFieldName = (fieldName) => {
  return fieldName
    .replace('SBQQ__', '')
    .replace('__c', '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

// --- FieldDisplay component using shared renderFields ---
function FieldDisplay({ fields }) {
  // Filter out null/undefined/empty values
  const filteredFields = Object.fromEntries(
    Object.entries(fields || {}).filter(([, value]) => value !== null && value !== undefined && value !== '')
  );

  return renderFields(filteredFields, { fieldSpacing: 1 });
}
FieldDisplay.propTypes = { fields: PropTypes.object };

// --- Helper function to filter fields based on visibility config ---
const getVisibleFields = (allFields, objectName, visibilityConfig, orgType) => {
  // `orgType` will be 'cpq' or 'rc'
  const configSection = visibilityConfig[orgType] || []; // Select the correct section
  const config = configSection.find((c) => c.objectName === objectName);

  if (!config || !config.displayColumns) {
    console.warn(`Visibility config missing 'displayColumns' for object: ${objectName}, showing all fields`);
    // If no config, return all fields instead of empty object
    return allFields || {};
  }

  const allowedFields = new Set(config.displayColumns.split(',').map((f) => f.trim()));
  return Object.fromEntries(Object.entries(allFields || {}).filter(([key]) => allowedFields.has(key)));
};

// --- Sub-component for template line columns ---
function TemplateLineColumnsSection({ columns, visibilityConfig, orgType }) {
  const [isTemplateColumnsExpanded, setIsTemplateColumnsExpanded] = useState(false);
  const [expandedColumns, setExpandedColumns] = useState({});

  const toggleColumn = (columnId) => {
    setExpandedColumns((prev) => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  // Safety checks
  if (!columns || !Array.isArray(columns)) {
    return (
      <ArgonBox p={3} textAlign="center">
        <ArgonTypography variant="body2" color="textSecondary">
          No template line columns available.
        </ArgonTypography>
      </ArgonBox>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <ArgonBox
        p={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => setIsTemplateColumnsExpanded(!isTemplateColumnsExpanded)}
        sx={{ cursor: 'pointer' }}
      >
        <ArgonTypography variant="h6" fontWeight="medium" color="secondary">
          Template Line Columns ({columns.length})
        </ArgonTypography>
        <IconButton size="small">{isTemplateColumnsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </ArgonBox>
      <Collapse in={isTemplateColumnsExpanded} timeout="auto" unmountOnExit>
        <ArgonBox p={2} sx={{ borderTop: `1px solid ${grey[300]}` }}>
          {columns.map((column, index) => {
            // Safety checks for column data
            if (!column) {
              return null;
            }

            const columnId = column.id || index;
            const isColumnExpanded = expandedColumns[columnId] || false;
            const visibleColumnFields = getVisibleFields(
              column.fields || {},
              'SBQQ__LineColumn__c',
              visibilityConfig,
              orgType
            );
            const hasColumnFields = Object.keys(visibleColumnFields).length > 0;

            return (
              <Card key={columnId} sx={{ mb: 1 }}>
                <ArgonBox
                  p={1.5}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  onClick={() => toggleColumn(columnId)}
                  sx={{ cursor: 'pointer' }}
                >
                  <ArgonTypography variant="h6" fontWeight="medium" color="secondary">
                    {column.name} ({column.fields?.SBQQ__FieldName__c || 'No field'})
                  </ArgonTypography>
                  {hasColumnFields && (
                    <IconButton size="small">{isColumnExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                  )}
                </ArgonBox>
                {hasColumnFields && (
                  <Collapse in={isColumnExpanded} timeout="auto" unmountOnExit>
                    <ArgonBox p={1.5} sx={{ borderTop: `1px solid ${grey[200]}` }}>
                      <FieldDisplay fields={visibleColumnFields} />
                    </ArgonBox>
                  </Collapse>
                )}
              </Card>
            );
          })}
        </ArgonBox>
      </Collapse>
    </Card>
  );
}

// --- Sub-component for template sections ---
function TemplateSectionsSection({ sections, visibilityConfig, orgType }) {
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedContent, setExpandedContent] = useState({});
  const [expandedColumns, setExpandedColumns] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const toggleContent = (sectionId) => {
    setExpandedContent((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const toggleColumns = (sectionId) => {
    setExpandedColumns((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Safety checks
  if (!sections || !Array.isArray(sections)) {
    return (
      <ArgonBox p={3} textAlign="center">
        <ArgonTypography variant="body2" color="textSecondary">
          No template sections available.
        </ArgonTypography>
      </ArgonBox>
    );
  }

  return (
    <ArgonBox>
      {sections.length > 0 ? (
        sections.map((section, index) => {
          // Safety checks for section data
          if (!section || !section.templateSections) {
            return null;
          }

          const sectionId = section.templateSections.id || index;
          const isSectionExpanded = expandedSections[sectionId] || false;
          const isContentExpanded = expandedContent[sectionId] || false;
          const isColumnsExpanded = expandedColumns[sectionId] || false;

          const visibleSectionFields = getVisibleFields(
            section.templateSections.fields || {},
            'SBQQ__TemplateSection__c',
            visibilityConfig,
            orgType
          );

          const visibleContentFields = section.templateContent
            ? getVisibleFields(
                section.templateContent.fields || {},
                'SBQQ__TemplateContent__c',
                visibilityConfig,
                orgType
              )
            : {};

          const hasContentFields = Object.keys(visibleContentFields).length > 0;
          const hasLineColumns =
            section.lineColumns && Array.isArray(section.lineColumns) && section.lineColumns.length > 0;

          return (
            <Card key={sectionId} sx={{ mb: 2 }}>
              <ArgonBox
                p={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                onClick={() => toggleSection(sectionId)}
                sx={{ cursor: 'pointer' }}
              >
                <ArgonTypography variant="h6" fontWeight="medium" color="secondary">
                  {section.templateSections.name}
                </ArgonTypography>
                <IconButton size="small">{isSectionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
              </ArgonBox>

              <Collapse in={isSectionExpanded} timeout="auto" unmountOnExit>
                <ArgonBox p={2} sx={{ borderTop: `1px solid ${grey[300]}` }}>
                  {/* Section Fields */}
                  <ArgonBox mb={3}>
                    <ArgonTypography variant="h6" fontWeight="medium" mb={1} color="secondary">
                      Section Settings
                    </ArgonTypography>
                    <FieldDisplay fields={visibleSectionFields} />
                  </ArgonBox>

                  {/* Template Content - Expandable */}
                  {section.templateContent && hasContentFields && (
                    <ArgonBox mb={3}>
                      <ArgonBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        onClick={() => toggleContent(sectionId)}
                        sx={{ cursor: 'pointer', mb: 1 }}
                      >
                        <ArgonTypography variant="h6" fontWeight="medium" color="secondary">
                          Template Content
                        </ArgonTypography>
                        <IconButton size="small">
                          {isContentExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </ArgonBox>
                      <Collapse in={isContentExpanded} timeout="auto" unmountOnExit>
                        <FieldDisplay fields={visibleContentFields} />
                      </Collapse>
                    </ArgonBox>
                  )}

                  {/* Section Line Columns - Expandable */}
                  {hasLineColumns && (
                    <ArgonBox>
                      <ArgonBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        onClick={() => toggleColumns(sectionId)}
                        sx={{ cursor: 'pointer', mb: 1 }}
                      >
                        <ArgonTypography variant="h6" fontWeight="medium" color="secondary">
                          Line Columns ({section.lineColumns.length})
                        </ArgonTypography>
                        <IconButton size="small">
                          {isColumnsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </ArgonBox>
                      <Collapse in={isColumnsExpanded} timeout="auto" unmountOnExit>
                        {section.lineColumns.map((column, columnIndex) => {
                          const columnId = column.id || columnIndex;
                          const visibleColumnFields = getVisibleFields(
                            column.fields,
                            'SBQQ__LineColumn__c',
                            visibilityConfig,
                            orgType
                          );
                          const hasColumnFields = Object.keys(visibleColumnFields).length > 0;

                          return (
                            <Card key={columnId} sx={{ mb: 1, ml: 2 }}>
                              <ArgonBox
                                p={1.5}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ cursor: 'default' }}
                              >
                                <ArgonTypography variant="h6" fontWeight="medium" color="secondary">
                                  {column.name} ({column.fields?.SBQQ__FieldName__c || 'No field'})
                                </ArgonTypography>
                                {hasColumnFields && (
                                  <ArgonTypography variant="caption" color="textSecondary">
                                    {Object.keys(visibleColumnFields).length} fields
                                  </ArgonTypography>
                                )}
                              </ArgonBox>
                            </Card>
                          );
                        })}
                      </Collapse>
                    </ArgonBox>
                  )}
                </ArgonBox>
              </Collapse>
            </Card>
          );
        })
      ) : (
        <ArgonTypography variant="body2">No template sections defined.</ArgonTypography>
      )}
    </ArgonBox>
  );
}

// --- Sub-component for section line columns ---
function SectionLineColumnsSection({ sections, visibilityConfig, orgType }) {
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedColumns, setExpandedColumns] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const toggleColumn = (columnId) => {
    setExpandedColumns((prev) => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  // Safety checks
  if (!sections || !Array.isArray(sections)) {
    return (
      <ArgonBox p={3} textAlign="center">
        <ArgonTypography variant="body2" color="textSecondary">
          No section line columns available.
        </ArgonTypography>
      </ArgonBox>
    );
  }

  return (
    <ArgonBox>
      <ArgonTypography variant="h6" fontWeight="medium" mb={2} color="secondary">
        Section Line Columns
      </ArgonTypography>
      {sections.map((section, sectionIndex) => {
        // Safety checks for section data
        if (
          !section ||
          !section.templateSections ||
          !section.lineColumns ||
          !Array.isArray(section.lineColumns) ||
          section.lineColumns.length === 0
        ) {
          return null;
        }

        const sectionId = section.templateSections?.id || sectionIndex;
        const isSectionExpanded = expandedSections[sectionId] || false;

        return (
          <Card key={sectionId} sx={{ mb: 2 }}>
            <ArgonBox
              p={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              onClick={() => toggleSection(sectionId)}
              sx={{ cursor: 'pointer' }}
            >
              <ArgonTypography variant="h6" fontWeight="medium" color="secondary">
                {section.templateSections?.name} - Line Columns ({section.lineColumns.length})
              </ArgonTypography>
              <IconButton size="small">{isSectionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
            </ArgonBox>
            <Collapse in={isSectionExpanded} timeout="auto" unmountOnExit>
              <ArgonBox p={2} sx={{ borderTop: `1px solid ${grey[300]}` }}>
                {section.lineColumns.map((column, columnIndex) => {
                  // Safety checks for column data
                  if (!column) {
                    return null;
                  }

                  const columnId = column.id || columnIndex;
                  const isColumnExpanded = expandedColumns[columnId] || false;
                  const visibleColumnFields = getVisibleFields(
                    column.fields || {},
                    'SBQQ__LineColumn__c',
                    visibilityConfig,
                    orgType
                  );
                  const hasColumnFields = Object.keys(visibleColumnFields).length > 0;

                  return (
                    <Card key={columnId} sx={{ mb: 1 }}>
                      <ArgonBox
                        p={1.5}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        onClick={() => toggleColumn(columnId)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <ArgonTypography variant="h6" fontWeight="medium" color="secondary">
                          {column.name} ({column.fields?.SBQQ__FieldName__c || 'No field'})
                        </ArgonTypography>
                        {hasColumnFields && (
                          <IconButton size="small">
                            {isColumnExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        )}
                      </ArgonBox>
                      {hasColumnFields && (
                        <Collapse in={isColumnExpanded} timeout="auto" unmountOnExit>
                          <ArgonBox p={1.5} sx={{ borderTop: `1px solid ${grey[200]}` }}>
                            <FieldDisplay fields={visibleColumnFields} />
                          </ArgonBox>
                        </Collapse>
                      )}
                    </Card>
                  );
                })}
              </ArgonBox>
            </Collapse>
          </Card>
        );
      })}
    </ArgonBox>
  );
}

// --- Sub-component for each template to manage its own state ---
function TemplateDetail({ template, orgType, onSelect, isSelected }) {
  const { visibilityConfig } = useVisibility();
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  // Use useMemo to prevent re-filtering on every render - must be before early return
  const visibleQuoteFields = useMemo(() => {
    if (!template || !template.quoteTemplate) {
      return {};
    }
    return getVisibleFields(template.quoteTemplate.fields || {}, 'SBQQ__QuoteTemplate__c', visibilityConfig, orgType);
  }, [template, visibilityConfig, orgType]);

  // Safety checks for template data
  if (!template || !template.quoteTemplate) {
    return (
      <Card sx={{ mb: 2 }}>
        <ArgonBox p={2} textAlign="center">
          <ArgonTypography variant="body2" color="textSecondary">
            Template data not available.
          </ArgonTypography>
        </ArgonBox>
      </Card>
    );
  }

  const { name: templateName } = template.quoteTemplate;

  return (
    <Card sx={{ mb: 1.5, overflow: 'hidden' }}>
      <ArgonBox
        p={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={(e) => {
          // Only handle row click if not clicking on checkbox or expand button
          if (!e.target.closest('input[type="checkbox"]') && !e.target.closest('button')) {
            setIsExpanded(!isExpanded);
          }
        }}
        sx={{ cursor: 'pointer' }}
      >
        <ArgonBox display="flex" alignItems="center">
          <Checkbox
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onSelect(template, e.target.checked);
            }}
            size="small"
            sx={{ zIndex: 10, position: 'relative' }}
          />
          <ArgonTypography variant="body2" fontWeight="medium" sx={{ color: grey[600], ml: 1 }}>
            {templateName}
          </ArgonTypography>
        </ArgonBox>
        <IconButton
          aria-expanded={isExpanded}
          aria-label="expand"
          size="small"
          sx={{ zIndex: 10, position: 'relative' }}
        >
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </ArgonBox>
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <ArgonBox sx={{ borderTop: `1px solid ${grey[300]}` }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
            <Tab label="General Settings" />
            <Tab label="Template Sections" />
            <Tab label="Line Columns" />
          </Tabs>
          <ArgonBox p={2}>
            {activeTab === 0 && <FieldDisplay fields={visibleQuoteFields} />}
            {activeTab === 1 && (
              <TemplateSectionsSection
                sections={template.templateSections}
                visibilityConfig={visibilityConfig}
                orgType={orgType}
              />
            )}
            {activeTab === 2 && (
              <ArgonBox>
                {/* Template-level line columns */}
                {template.lineColumns && template.lineColumns.length > 0 && (
                  <TemplateLineColumnsSection
                    columns={template.lineColumns}
                    visibilityConfig={visibilityConfig}
                    orgType={orgType}
                  />
                )}

                {/* Section-level line columns */}
                {template.templateSections && template.templateSections.length > 0 && (
                  <SectionLineColumnsSection
                    sections={template.templateSections}
                    visibilityConfig={visibilityConfig}
                    orgType={orgType}
                  />
                )}

                {/* Show message if no line columns exist */}
                {(!template.lineColumns || template.lineColumns.length === 0) &&
                  (!template.templateSections ||
                    template.templateSections.every((s) => !s.lineColumns || s.lineColumns.length === 0)) && (
                    <ArgonTypography variant="body2">No line columns defined.</ArgonTypography>
                  )}
              </ArgonBox>
            )}
          </ArgonBox>
        </ArgonBox>
      </Collapse>
    </Card>
  );
}
TemplateDetail.propTypes = { template: PropTypes.object.isRequired };

// --- Sub-component for each term to manage its own state ---
function TermDetail({ term, orgType, onSelect, isSelected }) {
  const { visibilityConfig } = useVisibility();
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  // Use useMemo to prevent re-filtering on every render
  const visibleTermFields = useMemo(() => {
    if (!term || !term.fields) {
      return {};
    }
    return getVisibleFields(term.fields || {}, 'SBQQ__QuoteTerm__c', visibilityConfig, orgType);
  }, [term, visibilityConfig, orgType]);

  // Safety checks for term data
  if (!term) {
    return (
      <Card sx={{ mb: 2 }}>
        <ArgonBox p={2} textAlign="center">
          <ArgonTypography variant="body2" color="textSecondary">
            Term data not available.
          </ArgonTypography>
        </ArgonBox>
      </Card>
    );
  }

  // Get counts for tab labels
  const subTermsCount = (term.subTerms && Array.isArray(term.subTerms)) ? term.subTerms.length : 0;
  const conditionsCount = (term.termConditions && Array.isArray(term.termConditions)) ? term.termConditions.length : 0;
  const modifiedTermsCount = (term.modifiedTerms && Array.isArray(term.modifiedTerms)) ? term.modifiedTerms.length : 0;

  // Always show all tabs, even if empty
  const tabs = [
    { label: 'Term Fields', key: 'fields' },
    { label: `Sub Terms (${subTermsCount})`, key: 'subTerms' },
    { label: `Conditions (${conditionsCount})`, key: 'conditions' },
    { label: `Modified Terms (${modifiedTermsCount})`, key: 'modifiedTerms' },
  ];

  return (
    <Card sx={{ mb: 1.5, overflow: 'hidden' }}>
      <ArgonBox
        p={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={(e) => {
          // Only handle row click if not clicking on checkbox or expand button
          if (!e.target.closest('input[type="checkbox"]') && !e.target.closest('button')) {
            setIsExpanded(!isExpanded);
          }
        }}
        sx={{ cursor: 'pointer' }}
      >
        <ArgonBox display="flex" alignItems="center">
          <Checkbox
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onSelect(term, e.target.checked);
            }}
            size="small"
            sx={{ zIndex: 10, position: 'relative' }}
          />
          <ArgonTypography variant="body2" fontWeight="medium" sx={{ color: grey[600], ml: 1 }}>
            {term.name || 'Unnamed Term'}
          </ArgonTypography>
          {term.fields?.SBQQ__Type__c && (
            <Chip
              label={term.fields.SBQQ__Type__c}
              size="small"
              color={term.fields.SBQQ__Type__c === 'Standard' ? 'primary' : 'secondary'}
              sx={{ ml: 2 }}
            />
          )}
        </ArgonBox>
        <IconButton
          aria-expanded={isExpanded}
          aria-label="expand"
          size="small"
          sx={{ zIndex: 10, position: 'relative' }}
        >
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </ArgonBox>
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <ArgonBox sx={{ borderTop: `1px solid ${grey[300]}` }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
            {tabs.map((tab, index) => (
              <Tab key={tab.key} label={tab.label} />
            ))}
          </Tabs>
          <ArgonBox p={2}>
            {/* Term Fields Tab */}
            {tabs[activeTab]?.key === 'fields' && (
              <ArgonBox>
                <ArgonTypography variant="h6" fontWeight="medium" mb={2} color="secondary">
                  Quote Term Details
                </ArgonTypography>
                <FieldDisplay fields={visibleTermFields} />
              </ArgonBox>
            )}
            
            {/* Sub Terms Tab */}
            {tabs[activeTab]?.key === 'subTerms' && (
              <ArgonBox>
                <ArgonTypography variant="h6" fontWeight="medium" mb={2} color="secondary">
                  Sub Terms
                </ArgonTypography>
                {subTermsCount > 0 ? (
                  term.subTerms.map((subTerm, index) => (
                    <Card key={subTerm.sfId || index} sx={{ mb: 2 }}>
                      <ArgonBox p={2}>
                        <ArgonTypography variant="body2" fontWeight="bold" mb={1}>
                          {subTerm.name || `Sub Term ${index + 1}`}
                        </ArgonTypography>
                        <FieldDisplay fields={getVisibleFields(subTerm.fields || {}, 'SBQQ__QuoteTerm__c', visibilityConfig, orgType)} />
                      </ArgonBox>
                    </Card>
                  ))
                ) : (
                  <ArgonBox textAlign="center" p={3}>
                    <ArgonTypography variant="body2" color="textSecondary">
                      This quote term has no sub terms configured.
                    </ArgonTypography>
                  </ArgonBox>
                )}
              </ArgonBox>
            )}

            {/* Term Conditions Tab */}
            {tabs[activeTab]?.key === 'conditions' && (
              <ArgonBox>
                <ArgonTypography variant="h6" fontWeight="medium" mb={2} color="secondary">
                  Term Conditions
                </ArgonTypography>
                {conditionsCount > 0 ? (
                  term.termConditions.map((condition, index) => (
                    <Card key={condition.sfId || index} sx={{ mb: 2 }}>
                      <ArgonBox p={2}>
                        <ArgonTypography variant="body2" fontWeight="bold" mb={1}>
                          {condition.name || `Condition ${index + 1}`}
                        </ArgonTypography>
                        <FieldDisplay fields={getVisibleFields(condition.fields || {}, 'SBQQ__TermCondition__c', visibilityConfig, orgType)} />
                      </ArgonBox>
                    </Card>
                  ))
                ) : (
                  <ArgonBox textAlign="center" p={3}>
                    <ArgonTypography variant="body2" color="textSecondary">
                      No term conditions defined for this quote term.
                    </ArgonTypography>
                  </ArgonBox>
                )}
              </ArgonBox>
            )}

            {/* Modified Terms Tab */}
            {tabs[activeTab]?.key === 'modifiedTerms' && (
              <ArgonBox>
                <ArgonTypography variant="h6" fontWeight="medium" mb={2} color="secondary">
                  Modified Terms
                </ArgonTypography>
                {modifiedTermsCount > 0 ? (
                  term.modifiedTerms.map((modTerm, index) => (
                    <Card key={modTerm.sfId || index} sx={{ mb: 2 }}>
                      <ArgonBox p={2}>
                        <ArgonTypography variant="body2" fontWeight="bold" mb={1}>
                          {modTerm.name || `Modified Term ${index + 1}`}
                        </ArgonTypography>
                        <FieldDisplay fields={getVisibleFields(modTerm.fields || {}, 'SBQQ__QuoteTerm__c', visibilityConfig, orgType)} />
                      </ArgonBox>
                    </Card>
                  ))
                ) : (
                  <ArgonBox textAlign="center" p={3}>
                    <ArgonTypography variant="body2" color="textSecondary">
                      No modified terms associated with this quote term.
                    </ArgonTypography>
                  </ArgonBox>
                )}
              </ArgonBox>
            )}
          </ArgonBox>
        </ArgonBox>
      </Collapse>
    </Card>
  );
}
TermDetail.propTypes = { 
  term: PropTypes.object.isRequired,
  orgType: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
};

// --- Main Quote Component ---
const lifecycleSettingsFields = {
  'Expiration Policy': '30 days',
  'Auto-Renew': 'Disabled',
  'Default Subscription Term': '12 months',
  'Default Start Date Behavior': 'Start date = Quote creation date',
};

function Quote({ quoteTemplates, quoteTerms, loadingTemplates, selectedOrgId, loading, orgType, onItemSelect, selectedItems }) {
  const [lifecycleExpanded, setLifecycleExpanded] = useState(false);
  const [templatesExpanded, setTemplatesExpanded] = useState(true);
  const [quoteTermsExpanded, setQuoteTermsExpanded] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectedTerms, setSelectedTerms] = useState([]);
  
  // Search and pagination for templates
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [templatePage, setTemplatePage] = useState(0);
  const templatesPerPage = 8;

  // Search and pagination for terms
  const [termSearchTerm, setTermSearchTerm] = useState('');
  const [termPage, setTermPage] = useState(0);
  const termsPerPage = 8;

  // Sync with parent selected items
  React.useEffect(() => {
    setSelectedTemplates(selectedItems?.quoteTemplates || []);
    setSelectedTerms(selectedItems?.quoteTerms || []);
  }, [selectedItems]);

  const handleTemplateSelect = (template, isSelected) => {
    let newSelection;
    if (isSelected) {
      newSelection = [...selectedTemplates, template];
    } else {
      // Use template.quoteTemplate.sfId for comparison
      newSelection = selectedTemplates.filter((t) => t.quoteTemplate?.sfId !== template.quoteTemplate?.sfId);
    }
    setSelectedTemplates(newSelection);
    onItemSelect('quoteTemplates', newSelection);
  };

  const isTemplateSelected = (template) => {
    return selectedTemplates.some((t) => t.quoteTemplate?.sfId === template.quoteTemplate?.sfId);
  };

  const handleTermSelect = (term, isSelected) => {
    let newSelection;
    if (isSelected) {
      newSelection = [...selectedTerms, term];
    } else {
      newSelection = selectedTerms.filter((t) => t.sfId !== term.sfId);
    }
    setSelectedTerms(newSelection);
    onItemSelect('quoteTerms', newSelection);
  };

  const isTermSelected = (term) => {
    return selectedTerms.some((t) => t.sfId === term.sfId);
  };

  const renderTemplatesSection = () => {
    if (loading) {
      return (
        <ArgonBox display="flex" justifyContent="center" alignItems="center" p={3}>
          <CircularProgress size={24} />
          <ArgonTypography variant="body2" sx={{ ml: 2 }}>
            Loading...
          </ArgonTypography>
        </ArgonBox>
      );
    }

    if (!selectedOrgId) {
      return (
        <ArgonBox p={4} textAlign="center" display="flex" flexDirection="column" alignItems="center">
          <InfoIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
          <ArgonTypography variant="h6" color="text" fontWeight="medium">
            Please select a CPQ Organization to view templates.
          </ArgonTypography>
        </ArgonBox>
      );
    }

    if (loadingTemplates) {
      return (
        <ArgonBox display="flex" justifyContent="center" alignItems="center" p={3}>
          <CircularProgress size={24} />
          <ArgonTypography variant="body2" sx={{ ml: 2 }}>
            Loading Quote Templates...
          </ArgonTypography>
        </ArgonBox>
      );
    }

    if (!quoteTemplates || !Array.isArray(quoteTemplates) || quoteTemplates.length === 0) {
      return (
        <ArgonBox p={3} pt={0} textAlign="center">
          <ArgonTypography variant="body2" color="text">
            No quote templates found for this organization.
          </ArgonTypography>
        </ArgonBox>
      );
    }

    // Filter and paginate templates
    const filteredTemplates = quoteTemplates.filter((template) => {
      if (!templateSearchTerm) return true;
      const searchLower = templateSearchTerm.toLowerCase();
      return template.quoteTemplate?.name?.toLowerCase().includes(searchLower);
    });

    const templatePageCount = Math.ceil(filteredTemplates.length / templatesPerPage);
    const paginatedTemplates = filteredTemplates.slice(
      templatePage * templatesPerPage,
      (templatePage + 1) * templatesPerPage
    );

    return (
      <ArgonBox p={2} pt={1}>
        {/* Search Bar */}
        <ArgonBox mb={2} display="flex" justifyContent="flex-end">
          <TextField
            size="medium"
            placeholder="Search quote templates..."
            value={templateSearchTerm}
            onChange={(e) => {
              setTemplateSearchTerm(e.target.value);
              setTemplatePage(0); // Reset to first page on search
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: '250px' }}
          />
        </ArgonBox>

        {/* Templates List */}
        {paginatedTemplates.length > 0 ? (
          <>
            {paginatedTemplates.map((template) => {
              // Safety checks for template data
              if (!template || !template.quoteTemplate) {
                return null;
              }

              return (
                <TemplateDetail
                  key={template.quoteTemplate.id || template.id || Math.random()}
                  template={template}
                  orgType={orgType}
                  onSelect={handleTemplateSelect}
                  isSelected={isTemplateSelected(template)}
                />
              );
            })}

            {/* Pagination */}
            {templatePageCount > 1 && (
              <ArgonBox display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={templatePageCount}
                  page={templatePage + 1}
                  onChange={(e, page) => setTemplatePage(page - 1)}
                  color="primary"
                />
              </ArgonBox>
            )}
          </>
        ) : (
          <ArgonBox p={3} textAlign="center">
            <ArgonTypography variant="body2" color="text">
              {templateSearchTerm ? 'No quote templates match your search.' : 'No quote templates found.'}
            </ArgonTypography>
          </ArgonBox>
        )}
      </ArgonBox>
    );
  };

  const renderQuoteTermsSection = () => {
    if (loading) {
      return (
        <ArgonBox display="flex" justifyContent="center" alignItems="center" p={3}>
          <CircularProgress size={24} />
          <ArgonTypography variant="body2" sx={{ ml: 2 }}>
            Loading...
          </ArgonTypography>
        </ArgonBox>
      );
    }

    if (!selectedOrgId) {
      return (
        <ArgonBox p={4} textAlign="center" display="flex" flexDirection="column" alignItems="center">
          <InfoIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
          <ArgonTypography variant="h6" color="text" fontWeight="medium">
            Please select a CPQ Organization to view quote terms.
          </ArgonTypography>
        </ArgonBox>
      );
    }

    if (loadingTemplates) {
      return (
        <ArgonBox display="flex" justifyContent="center" alignItems="center" p={3}>
          <CircularProgress size={24} />
          <ArgonTypography variant="body2" sx={{ ml: 2 }}>
            Loading Quote Terms...
          </ArgonTypography>
        </ArgonBox>
      );
    }

    if (!quoteTerms || !Array.isArray(quoteTerms) || quoteTerms.length === 0) {
      return (
        <ArgonBox p={3} pt={0} textAlign="center">
          <ArgonTypography variant="body2" color="text">
            No quote terms found for this organization.
          </ArgonTypography>
        </ArgonBox>
      );
    }

    // Filter and paginate terms
    const filteredTerms = quoteTerms.filter((term) => {
      if (!termSearchTerm) return true;
      const searchLower = termSearchTerm.toLowerCase();
      return term.name?.toLowerCase().includes(searchLower);
    });

    const termPageCount = Math.ceil(filteredTerms.length / termsPerPage);
    const paginatedTerms = filteredTerms.slice(
      termPage * termsPerPage,
      (termPage + 1) * termsPerPage
    );

    return (
      <ArgonBox p={2} pt={1}>
        {/* Search Bar */}
        <ArgonBox mb={2} display="flex" justifyContent="flex-end">
          <TextField
            size="medium"
            placeholder="Search quote terms..."
            value={termSearchTerm}
            onChange={(e) => {
              setTermSearchTerm(e.target.value);
              setTermPage(0); // Reset to first page on search
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: '250px' }}
          />
        </ArgonBox>

        {/* Terms List */}
        {paginatedTerms.length > 0 ? (
          <>
            {paginatedTerms.map((term) => {
              // Safety checks for term data
              if (!term) {
                return null;
              }

              return (
                <TermDetail
                  key={term.sfId || term.id || Math.random()}
                  term={term}
                  orgType={orgType}
                  onSelect={handleTermSelect}
                  isSelected={isTermSelected(term)}
                />
              );
            })}

            {/* Pagination */}
            {termPageCount > 1 && (
              <ArgonBox display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={termPageCount}
                  page={termPage + 1}
                  onChange={(e, page) => setTermPage(page - 1)}
                  color="primary"
                />
              </ArgonBox>
            )}
          </>
        ) : (
          <ArgonBox p={3} textAlign="center">
            <ArgonTypography variant="body2" color="text">
              {termSearchTerm ? 'No quote terms match your search.' : 'No quote terms found.'}
            </ArgonTypography>
          </ArgonBox>
        )}
      </ArgonBox>
    );
  };

  return (
    <ArgonBox>
      {/* Cards remain the same */}
      <Card sx={{ mb: 2, boxShadow: 'md' }}>
        <ArgonBox
          p={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          onClick={() => setLifecycleExpanded(!lifecycleExpanded)}
          sx={{ cursor: 'pointer' }}
        >
          <ArgonTypography variant="body2" fontWeight="bold" color="secondary">
            1. Quote Lifecycle Settings
          </ArgonTypography>
          <IconButton aria-expanded={lifecycleExpanded} aria-label="expand" size="small">
            {lifecycleExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </ArgonBox>
        <Collapse in={lifecycleExpanded} timeout="auto" unmountOnExit>
          <ArgonBox p={3} pt={0}>
            {Object.entries(lifecycleSettingsFields).map(([key, value]) => (
              <ArgonBox key={key} mb={1} width="100%">
                <Grid container alignItems="center" spacing={0}>
                  <Grid item xs={7}>
                    <ArgonTypography
                      variant="caption"
                      fontSize={13}
                      fontWeight="medium"
                      color="textSecondary"
                      align="left"
                      sx={{ wordWrap: 'break-word', color: grey[700], ml: 3 }}
                    >
                      {key}
                    </ArgonTypography>
                  </Grid>
                  <Grid item xs={1} sx={{ textAlign: 'center' }}>
                    <Divider orientation="vertical" flexItem />
                  </Grid>
                  <Grid item xs={3} sx={{ textAlign: 'center' }}>
                    <ArgonBox
                      bgcolor={grey[200]}
                      color={grey[700]}
                      p={1}
                      borderRadius="md"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{ width: '100%', boxSizing: 'border-box', minHeight: '36px' }}
                    >
                      <ArgonTypography
                        variant="caption"
                        fontWeight="bold"
                        sx={{ wordWrap: 'break-word', width: '100%', textAlign: 'center' }}
                      >
                        {value || '-'}
                      </ArgonTypography>
                    </ArgonBox>
                  </Grid>
                </Grid>
              </ArgonBox>
            ))}
          </ArgonBox>
        </Collapse>
      </Card>

      <Card sx={{ mb: 2, boxShadow: 'md' }}>
        <ArgonBox
          p={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          onClick={() => setTemplatesExpanded(!templatesExpanded)}
          sx={{ cursor: 'pointer' }}
        >
          <ArgonTypography variant="body2" fontWeight="bold" color="secondary">
            2. Quote Templates
          </ArgonTypography>
          <IconButton aria-expanded={templatesExpanded} aria-label="expand" size="small">
            {templatesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </ArgonBox>
        <Collapse in={templatesExpanded} timeout="auto" unmountOnExit>
          {renderTemplatesSection()}
        </Collapse>
      </Card>

      <Card sx={{ mb: 2, boxShadow: 'md' }}>
        <ArgonBox
          p={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          onClick={() => setQuoteTermsExpanded(!quoteTermsExpanded)}
          sx={{ cursor: 'pointer' }}
        >
          <ArgonTypography variant="body2" fontWeight="bold" color="secondary">
            3. Quote Terms
          </ArgonTypography>
          <IconButton aria-expanded={quoteTermsExpanded} aria-label="expand" size="small">
            {quoteTermsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </ArgonBox>
        <Collapse in={quoteTermsExpanded} timeout="auto" unmountOnExit>
          {renderQuoteTermsSection()}
        </Collapse>
      </Card>
    </ArgonBox>
  );
}

Quote.propTypes = {
  quoteTemplates: PropTypes.array,
  quoteTerms: PropTypes.array,
  loadingTemplates: PropTypes.bool,
  selectedOrgId: PropTypes.string,
  loading: PropTypes.bool,
  orgType: PropTypes.string,
  onItemSelect: PropTypes.func,
  selectedItems: PropTypes.object,
};

export default Quote;
